/*
 * Copyright (c) 2024. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect} from "react";
import {useTranslation} from "react-i18next";
import {PersonType} from "types/enums/PersonType";
import {MinusIcon, PlusIcon} from "@heroicons/react/24/outline";
import clsx from "clsx";
import {toast} from "react-toastify";
import {UseFormSetValue} from "react-hook-form/dist/types";
import {BookingFilter} from "types/app";
import {Package} from "types/entities/Package";
import {PackageItemPersonType} from "types/enums/PackageItemPersonType";
import {PackageItem, PackageItemItemType} from "types/entities/PackageItem";
import {useTypedSelector} from "helpers/reducers";
import DriverCountPersonType from "pages/Front/Booking/components/BookingFilters/DriverCountPersonType";

interface DriversCountFilterItemProps {
    name: "children" | "adults" | "date" | "activity_ids" | "groupType";
    personType: PersonType;
    setValue: UseFormSetValue<BookingFilter>;
    register: any;
    isMinimalistic: boolean;
    watch: any;
    onChange: (type: string, value: string) => void;
    disabled?: boolean;
    isGiftCard?: boolean;
    isGroupTypeChildren?: boolean;
    isEvent?: boolean;
    isAction?: boolean;
    selectedPackage: Package | undefined | null;
}

const DriversCountFilterItem = (props: DriversCountFilterItemProps) => {
    const {t} = useTranslation();
    const valueButtonClassName =
        (props.disabled ? "bg-gray-700 cursor-not-allowed" : "bg-cta-800 text-contrast-800 cursor-pointer") +
        "  hover:no-underline text-3xl flex-grow text-center flex flex-row justify-center items-center min-w-[40px] sm:min-w-[44px]";
    // const [driversCount, setDriversCount] = useState<number>(0);
    const maxValue = 1000;
    const driversCount = props.watch(props.name);
    const bookingFilter = useTypedSelector(state => state.bookingState.bookingProcess.filter);

    function getDriversCountLimit(selectedPackage: Package, personType: PackageItemPersonType, limit: string) {
        //filter items and reduce their biggest or lowest value available
        const limitForDriversCount = selectedPackage.packageItems
            .filter(packageItem => packageItem.type != PackageItemItemType.Upsell)
            .filter(packageItem => packageItem.person_type === personType)
            .reduce((prev: number, cur: PackageItem) => {
                if (!prev) {
                    if (limit === "maximum") {
                        return cur.maximum_persons_count;
                    } else {
                        return cur.minimum_persons_count;
                    }
                }

                if (limit === "maximum") {
                    return Math.max(cur.maximum_persons_count, prev);
                } else {
                    return Math.min(cur.minimum_persons_count, prev);
                }
            }, 0);

        if (!limitForDriversCount) {
            switch (limit) {
                case "maximum":
                    return maxValue;
                case "minimum":
                    return 0;
                default:
                    return 0;
            }
        }
        switch (limit) {
            case "maximum":
            case "minimum":
                return limitForDriversCount;
            default:
                return 0;
        }
    }

    const getPackageItemPersonsType = (personType: PersonType): PackageItemPersonType => {
        switch (personType) {
            case PersonType.Adult:
                return PackageItemPersonType.Adult;
            case PersonType.Child:
                return PackageItemPersonType.Child;
            default:
                return PackageItemPersonType.Adult;
        }
    };

    const minimumDriversCount = props.selectedPackage ? getDriversCountLimit(props.selectedPackage, getPackageItemPersonsType(props.personType), "minimum") : 0;
    const maximumDriversCount = props.selectedPackage
        ? getDriversCountLimit(props.selectedPackage, getPackageItemPersonsType(props.personType), "maximum")
        : maxValue;

    const packageMaximum = props.selectedPackage?.max_drivers_count;

    const setDriversCountValue = (driversCountValue: string) => {
        if (isNaN(+driversCountValue)) {
            toast.warning(t("Enter numbers only"));
            return;
        }
        checkNewFilterValue(+driversCountValue);
    };

    const setDriversCountHandler = (newDriversCount: number) => {
        const newCount = +driversCount + newDriversCount;
        checkNewFilterValue(newCount);
    };

    const checkNewFilterValue = value => {
        const restValue = props.personType === PersonType.Child ? bookingFilter.adults : bookingFilter.children;
        if (packageMaximum) {
            if (restValue + value > packageMaximum) {
                toast.warning(t("cannot_take_more_persons", {count: packageMaximum}));
                return;
            }
        }

        if (value > maximumDriversCount) {
            toast.warning(t("cannot_take_more_persons", {count: packageMaximum}));
            return;
        }

        //if new count is equal to zero, set it
        if (value < 0 || value === 0) {
            setFilterValue(0);
            return;
        }

        //if new count is less than minimum, set minimum
        if (value < minimumDriversCount) {
            setFilterValue(minimumDriversCount);
            toast.warning(t("cannot_take_less_persons", {count: minimumDriversCount}));
            return;
        }

        //otherwise if new count is more than zero, set it
        if (value >= 0) {
            setFilterValue(value);
        }
    };

    const setFilterValue = value => {
        props.setValue(props.name, value + "");
        props.onChange(props.name, value + "");
    };

    useEffect(() => {
        if (minimumDriversCount > 0 && driversCount === 0) {
            setDriversCountHandler(minimumDriversCount);
        }
    }, [driversCount]);

    return (
        <div className={"DriversCountFilterItem"}>
            <div
                className={clsx(
                    props.disabled && "bg-opacity-50 opacity-50 grayscale cursor-not-allowed pointer-events-none select-none",
                    "flex flex-row justify-between items-center space-x-2 xl:space-x-4"
                )}
            >
                {props.isGiftCard ? (
                    <div className={"flex flex-col w-1/2 md:w-1/2"}>
                        <div className={"capitalize "}>
                            <b>{t("Gift cards")}</b>
                        </div>
                    </div>
                ) : (
                    <DriverCountPersonType
                        isAction={props.isAction}
                        isEvent={props.isEvent}
                        isGroupTypeChildren={props.isGroupTypeChildren}
                        isMinimalistic={props.isMinimalistic}
                        minimumDriversCount={minimumDriversCount}
                        personType={props.personType}
                    />
                )}

                <div
                    className={clsx(
                        props.disabled ? "border-gray-800" : "bg-cta-800",
                        "flex flex-row justify-between items-stretch h-10 w-1/2 md:w-1/2 max-w-[144px] border rounded-md overflow-hidden"
                    )}
                >
                    <button
                        className={valueButtonClassName}
                        id={"DecrementPersonsCount-" + props.personType}
                        type={"button"}
                        onClick={() => setDriversCountHandler(-1)}
                    >
                        <MinusIcon className={"pointer-events-none w-5 h-5"} />
                    </button>
                    <div className={"bg-white text-xl text-gray-800 bg-white leading-4 text-center flex flex-row justify-center items-center"}>
                        <input
                            className="shrink w-full px-1 text-center h-full text-black hover:outline-cta-800 hover:appearance-none	"
                            inputMode={"numeric"}
                            max={maximumDriversCount}
                            min={minimumDriversCount}
                            style={{
                                appearance: "textfield",
                                WebkitAppearance: "none",
                                MozAppearance: "textfield",
                            }}
                            type={"text"}
                            value={driversCount}
                            onChange={e => setDriversCountValue(e.target.value)}
                        />
                    </div>
                    <input {...props.register(props.name)} name={props.name} type="hidden" />
                    <button
                        className={valueButtonClassName}
                        id={"IncrementPersonsCount-" + props.personType}
                        type={"button"}
                        onClick={() => setDriversCountHandler(+1)}
                    >
                        <PlusIcon className={"pointer-events-none w-5 h-5"} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DriversCountFilterItem;
